import { supabase } from './supabase';
import type { LeadProductBron, KleedVorm } from './lead-product';

export type Brand = 'KLEDA' | 'CINCO' | 'VLOERKLEEDREINIGEN' | 'BANKREINIGEN' | 'SOFASAVER';
export type MarketingChannel =
  | 'WEBSITE' | 'PHONE' | 'EMAIL' | 'CHAT' | 'REFERRAL'
  | 'GOOGLE_ADS' | 'META_ADS' | 'ORGANIC_SEO' | 'DIRECT';

export interface CreateOfferteCustomerInput {
  firstName?: string | null;
  lastName: string;
  email?: string | null;
  phone?: string | null;
  company?: string | null;
  existingAccountId?: string | null;
  existingContactId?: string | null;
}

export interface CreateOfferteAddressInput {
  street?: string | null;
  houseNumber?: string | null;
  houseNumberSuffix?: string | null;
  postalCode?: string | null;
  city?: string | null;
}

export interface CreateOfferteItemInput {
  productCode?: string;
  vorm?: KleedVorm;       // verplicht voor vorm-based primaries; optioneel voor Kussen
  lengteCm?: number | null;
  breedteCm?: number | null;
  diameterCm?: number | null;
  oppervlakteM2?: number | null;
  aantal?: number | null; // verplicht voor count-based (Kussen)
  bijzonderheden?: string | null;
  externeReferentie?: string | null;
}

export type OfferteChannel = 'QUOTE' | 'ORDER';

export interface CreateOfferteInput {
  customer: CreateOfferteCustomerInput;
  address?: CreateOfferteAddressInput | null;
  brand: Brand;
  marketingChannel?: MarketingChannel;
  packageSku?: string | null;
  items: CreateOfferteItemInput[];
  bron?: LeadProductBron;
  sourceWerkOrderId?: string | null;
  channel?: OfferteChannel;
  /** Optionele handmatige korting op de hele offerte (0–100). Wordt server-side als Actie_Code__c='KORTING:<int>' gezet. */
  kortingPercentage?: number | null;
  /** Office-override: sta een ORDER zonder telefoonnummer toe (SF markeert Telefoon_Ontbreekt__c). */
  allowMissingPhone?: boolean;
}

export interface CreateOfferteResponse {
  success: boolean;
  errorCode?: string;
  errorMessage?: string;
  leadId?: string;
  leadnummer?: string;
  contactId?: string;
  productIds?: string[];
  /** Bij channel ORDER: de keten converteert synchroon en geeft de WO direct terug. */
  workOrderId?: string;
}

export async function createOfferte(input: CreateOfferteInput): Promise<CreateOfferteResponse> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;

  const response = await fetch('/api/offertes/create', {
    method: 'POST',
    headers,
    body: JSON.stringify(input),
  });
  const data = (await response.json().catch(() => ({}))) as CreateOfferteResponse;
  if (!response.ok && data.success !== true) {
    return {
      success: false,
      errorCode: data.errorCode || 'HTTP_ERROR',
      errorMessage: data.errorMessage || `Aanmaken mislukt (${response.status})`,
    };
  }
  return data;
}
